import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, Award, MapPin, Users, Mail, Facebook, Tent, 
  Car, Bike, Truck, CheckCircle, Phone, Send, 
  ArrowRight, ChevronDown, Star, DollarSign, 
  Calendar, Target, Shield, Bot
} from 'lucide-react';
import SEO from '../components/SEO';
import { organizationSchema, localBusinessSchema, serviceSchema, webSiteSchema } from '../utils/structuredData';
import { homeResults } from '../data/results';
import VideoBackground from '../components/VideoBackground';

const HomePage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={styles.page}>
      
      <SEO
        title="Automotive Event Marketing — Offsite, Tent & Mail"
        description="Driven2WIN: 6,500+ dealership events, $300K+ avg gross, all 50 states. Offsite events, tent setups, direct mail & Facebook campaigns. Guaranteed results."
        keywords="automotive event marketing, dealership sales events, offsite sales events, tent setups, direct mail dealership, golf cart dealer events, motorcycle dealer events, RV dealer events, nationwide event company, car dealer promotions"
        canonicalUrl="https://driven2win.com/"
        structuredData={[webSiteSchema, organizationSchema, localBusinessSchema, serviceSchema]}
      />
      
      {/* Hero Section */}
      <section style={styles.hero} className="hp-hero-section">
        {/* Video background — swap /videos/hero-bg.mp4 once downloaded */}
        <VideoBackground
          src="/videos/hero-bg.mp4"
          fallbackImage="/images/hero/hero-fallback.jpg"
          posterImage="/images/hero/hero-poster.jpg"
          overlayGradient="linear-gradient(135deg, rgba(26,31,58,0.82) 0%, rgba(15,17,24,0.75) 50%, rgba(227,30,36,0.15) 100%)"
        />
        <div style={styles.heroPattern} />
        
        <div style={styles.heroContent} className="hp-hero-content">
          <div style={styles.heroTextContainer}>
            <div style={styles.heroBadge} className="hp-hero-badge">
              <Award size={20} style={{ marginRight: '8px' }} />
              <span>NATIONWIDE LEADER OF ON-SITE AND OFF-SITE EVENTS</span>
            </div>
            
            <h1 style={styles.heroH1} className="hp-hero-h1">
              Drive <span style={{color: '#FFC300'}}>MASSIVE TRAFFIC</span> & Sales With Events That <em style={{color: '#FF6B35'}}>DOMINATE</em> Your Market!
            </h1>
            
            <p style={styles.heroSubheadline}>
              <strong>23+ years</strong> creating high-impact sales events for car & truck, golf cart, motorcycle, ATV, and RV dealers across America. <strong>We bring the crowds. You make the deals. Together, we move metal.</strong>
            </p>
            
            <div className="grid-3col" style={styles.heroStats}>
              <div style={styles.heroStat}>
                <div style={styles.heroStatNumber} className="hp-hero-stat-num">6,500+</div>
                <div style={styles.heroStatLabel} className="hp-hero-stat-label">Events Produced</div>
              </div>
              <div style={styles.heroStat}>
                <div style={styles.heroStatNumber} className="hp-hero-stat-num">$300K+</div>
                <div style={styles.heroStatLabel} className="hp-hero-stat-label">Avg Event Gross</div>
              </div>
              <div style={styles.heroStat}>
                <div style={styles.heroStatNumber} className="hp-hero-stat-num">Clean</div>
                <div style={styles.heroStatLabel} className="hp-hero-stat-label">Business No Heat</div>
              </div>
            </div>
            
            <div style={styles.heroCTAs}>
              <Link to="/contact#contact-form" className="btn-primary" style={styles.btnPrimary}>
                <DollarSign size={20} style={{ marginRight: '8px' }} />
                Get Your FREE Event Strategy Call
              </Link>
              <Link to="/services" className="btn-secondary" style={styles.btnSecondary}>
                See Event Packages
              </Link>
            </div>
            
            <div style={styles.guarantee}>
              <Shield size={20} color="#FFC300" style={{ marginRight: '8px' }} />
              <span><strong>GUARANTEED RESULTS</strong> or we keep working until you're satisfied</span>
            </div>
          </div>
        </div>
        
        <div style={styles.scrollIndicator}>
          <ChevronDown size={32} />
        </div>
      </section>

      {/* Explosive Stats */}
      <section style={styles.explosiveStats}>
        <div style={styles.container}>
          <div style={styles.explosiveStatGrid}>
            <ExplosiveStat icon={<Calendar />} number="23+" label="Years Dominating" color="#E31E24" />
            <ExplosiveStat icon={<MapPin />} number="50" label="States Served" color="#FF6B35" />
            <ExplosiveStat icon={<Users />} number="250+" label="Dealers Trust Us" color="#0080FF" />
            <ExplosiveStat icon={<Target />} number="6,500+" label="EVENTS PRODUCED" color="#FFC300" />
          </div>
        </div>
      </section>

      {/* Promise Banner */}
      <section style={styles.promiseBanner}>
        {/* Video background — swap /videos/promise-bg.mp4 once downloaded */}
        <VideoBackground
          src="/videos/promise-bg.mp4"
          fallbackImage="/images/hero/promise-fallback.jpg"
          overlayGradient="linear-gradient(135deg, rgba(26,31,58,0.88) 0%, rgba(15,17,24,0.92) 100%)"
        />
        <div style={{...styles.container, position:'relative', zIndex:1}}>
          <h2 style={styles.promiseH2}>
            <span style={{fontSize: '72px', display: 'block', marginBottom: '16px'}}>NO SMOKE.</span>
            <span style={{fontSize: '72px', display: 'block', color: '#FFC300'}}>ONLY RESULTS!</span>
          </h2>
          <p style={styles.promiseText}>
            We don't do weak promotions. We create <strong>HIGH-ENERGY automotive, golf cart, motorsports, and RV sales events</strong> with professional execution, massive turnout, and <strong>PROVEN RESULTS</strong> that fill your lot with traffic and move inventory FAST.
          </p>
        </div>
      </section>

      {/* Results Cards */}
      <section style={styles.resultsSection}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionH2}>
              REAL DEALERS. <span style={{color: '#E31E24'}}>REAL RESULTS.</span>
            </h2>
            <p style={styles.sectionSubheadline}>
              These are ACTUAL event results from dealerships across America
            </p>
          </div>
          
          <div style={styles.resultsGrid}>
            {homeResults.slice(0, 6).map((result, index) => (
              <ResultCard key={index} {...result} />
            ))}
          </div>
          
          <div style={{textAlign: 'center', marginTop: '48px'}}>
            <Link to="/event-results" className="btn-primary" style={styles.btnPrimary}>
              View All Event Results <ArrowRight size={20} style={{marginLeft: '8px'}} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section style={styles.servicesSection}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionH2}>
              <Zap size={48} style={{display: 'inline-block', color: '#FFC300', marginRight: '16px'}} />
              FULL-SERVICE EVENT MARKETING THAT <span style={{color: '#E31E24'}}>DELIVERS</span>
            </h2>
          </div>
          
          <div className="hp-services-grid-5" style={styles.servicesGrid}>
            <ServiceCard
              icon={<Tent size={50} />}
              title="Offsite Sales Events"
              description="Take your inventory where the buyers are! We create HIGH-TRAFFIC offsite events that pack the lot with motivated buyers ready to deal."
              link="/services/offsite-events"
              color="#E31E24"
            />
            
            <ServiceCard
              icon={<Award size={50} />}
              title="Tent Setups & Permitting"
              description="Complete turnkey setup! We handle EVERYTHING — permits, tents, logistics, signage. You focus on selling. We handle the rest."
              link="/services/tent-setups"
              color="#FF6B35"
            />
            
            <ServiceCard
              icon={<Mail size={50} />}
              title="Direct Mail That DRIVES Response"
              description="Forget boring mailers! Our campaigns create URGENCY and EXCITEMENT that fills your event with qualified buyers."
              link="/services/direct-mail"
              color="#0080FF"
            />
            
            <ServiceCard
              icon={<Facebook size={50} />}
              title="Powerful Facebook & Instagram Events"
              description="Amplify your reach! We create viral social campaigns that generate 100K-400K impressions and drive massive awareness, calls, traffic, and sales."
              link="/services/facebook-events"
              color="#FFC300"
            />

            <ServiceCard
              icon={<Bot size={50} />}
              title="AI BDC — Voice & SMS Agents"
              description="Never miss a lead! AI Voice & SMS Agents instantly answer calls, follow up, and book appointments 24/7 — in English and Spanish. Up to 60% appointment set rate."
              link="/contact"
              color="#8B5CF6"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={styles.testimonialsSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            WHAT DEALERS ARE <span style={{color: '#E31E24'}}>SAYING</span>
          </h2>
          
          <div style={styles.testimonialCard}>
            <div style={styles.starRating}>
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} fill="#FFC300" color="#FFC300" />
              ))}
            </div>
            
            <p style={styles.testimonialQuote}>
              "{testimonials[currentTestimonial].quote}"
            </p>
            
            <div style={styles.testimonialAuthor}>
              {testimonials[currentTestimonial].name && <div style={styles.testimonialName}>{testimonials[currentTestimonial].name}</div>}
              <div style={styles.testimonialTitle}>{testimonials[currentTestimonial].title}</div>
              <div style={styles.testimonialLocation}>{testimonials[currentTestimonial].location}</div>
            </div>
            
            <div style={styles.testimonialDots}>
              {testimonials.map((_, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  style={{
                    ...styles.testimonialDot,
                    background: index === currentTestimonial ? '#E31E24' : '#CCC',
                    cursor: 'pointer'
                  }}
                />
              ))}
            </div>
          </div>
          
          <div style={{textAlign: 'center', marginTop: '48px'}}>
            <Link to="/testimonials" className="btn-secondary" style={styles.btnSecondary}>
              Read All Testimonials
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={styles.finalCTA}>
        <div style={styles.container}>
          <h2 style={styles.finalCTAH2}>
            Ready to <span style={{color: '#FFC300'}}>PACK YOUR LOT</span> and <span style={{color: '#FFC300'}}>CRUSH YOUR SALES GOALS?</span>
          </h2>
          <p style={styles.finalCTAText}>
            Let's create a HIGH-IMPACT event that brings MASSIVE TRAFFIC and REAL RESULTS to your dealership!
          </p>
          <Link to="/contact#contact-form" className="btn-primary" style={{...styles.btnPrimary, fontSize: '24px', padding: '24px 48px'}}>
            <Phone size={24} style={{marginRight: '12px'}} />
            Call 904-638-7200 NOW!
          </Link>
          <p style={{color: '#FFFFFF', marginTop: '24px', fontSize: '16px'}}>
            Or fill out our contact form for a FREE event strategy call →
          </p>
        </div>
      </section>

      <style>{`
        @media (max-width: 968px) {
          /* Push hero content down so badge clears the fixed header+logo on ALL screen sizes */
          .hp-hero-section { padding-top: 80px !important; }
          .hp-hero-badge {
            max-width: 100% !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
            font-size: 11px !important;
            padding: 10px 16px !important;
            text-align: center !important;
          }
        }
        @media (max-width: 768px) {
          .hp-hero-h1 { font-size: 34px !important; line-height: 1.15 !important; letter-spacing: 1px !important; }
          .hp-hero-sub { font-size: 15px !important; }
          .hp-hero-stat-num { font-size: 22px !important; }
          .hp-hero-stats { gap: 16px !important; }
          .hp-hero-stat-label { font-size: 10px !important; letter-spacing: 0.5px !important; }
          .hp-section-h2 { font-size: 28px !important; letter-spacing: 1px !important; }
          .hp-cta-h2 { font-size: 28px !important; }
          .hp-results-grid { grid-template-columns: 1fr !important; }
          .hp-services-grid-5 { grid-template-columns: 1fr !important; }
          .hp-services-grid { grid-template-columns: 1fr !important; }
          .hp-hero-content { padding: 0 16px !important; }
          .hp-section-pad { padding: 60px 0 !important; }
          .hp-hero-ctas { flex-direction: column !important; align-items: stretch !important; }
          .hp-hero-ctas a { text-align: center !important; justify-content: center !important; }
          .hp-hero-section { padding: 80px 0 60px !important; }
          .hp-hero-badge { font-size: 10px !important; letter-spacing: 0.5px !important; }
          .hp-result-num { font-size: 24px !important; }
        }

        .btn-primary, .btn-secondary {
          display: inline-flex;
          align-items: center;
          text-decoration: none;
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(227, 30, 36, 0.5);
        }
        
        .btn-secondary:hover {
          background: #0080FF;
          color: white;
          transform: translateY(-3px);
        }
      `}</style>
    </div>
  );
};

// Components
const ExplosiveStat = ({ icon, number, label, color }) => (
  <div style={{...styles.explosiveStatItem, borderLeft: `5px solid ${color}`}}>
    <div style={{...styles.explosiveStatIcon, color: color}}>
      {React.cloneElement(icon, { size: 48 })}
    </div>
    <div>
      <div style={styles.explosiveStatNumber}>{number}</div>
      <div style={styles.explosiveStatLabel}>{label}</div>
    </div>
  </div>
);

const ResultCard = ({ location, units, gross }) => (
  <div style={styles.resultCard}>
    <div style={styles.resultCardTop}>
      <MapPin size={24} color="#FFC300" />
      <div style={styles.resultLocation}>{location}</div>
    </div>
    <div style={styles.resultStats}>
      <div style={styles.resultStat}>
        <div style={styles.resultNumber} className="hp-result-num">{units}</div>
        <div style={styles.resultLabel}>Units Sold</div>
      </div>
      <div style={styles.resultDivider} />
      <div style={styles.resultStat}>
        <div style={styles.resultNumber} className="hp-result-num">${(gross/1000).toFixed(0)}K</div>
        <div style={styles.resultLabel}>Gross Profit</div>
      </div>
    </div>
  </div>
);

const ServiceCard = ({ icon, title, description, link, color }) => (
  <div style={styles.serviceCard}>
    <div style={{...styles.serviceIcon, background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`}}>
      {React.cloneElement(icon, { color: 'white' })}
    </div>
    <h3 style={styles.serviceTitle}>{title}</h3>
    <p style={styles.serviceDescription}>{description}</p>
    <Link to={link} style={{...styles.serviceLink, color: color}}>
      Learn More <ArrowRight size={18} style={{marginLeft: '8px'}} />
    </Link>
  </div>
);

// Data

const testimonials = [
  {
    quote: "Driven2WIN brought us 127 appointments in 5 days and we sold 89 units with $542K in gross! This was THE BEST event we've ever run. The team is professional, the results are REAL, and we're booking them again next quarter.",
    name: "",
    title: "General Manager",
    location: "General Manager, Tampa, FL"
  },
  {
    quote: "I've worked with a lot of event companies over my 20 years in this business. Driven2WIN is hands-down the best. They handle EVERYTHING — permits, setup, promotion — and they GUARANTEE their numbers. We hit 112 units sold in one week!",
    name: "",
    title: "Owner",
    location: "Owner, Dallas, TX"
  },
  {
    quote: "What impressed me most was the TURNOUT. Every hour we had fresh customers walking in with the mailer. The energy was incredible. Our sales team was closing deals left and right. This is what a REAL event looks like!",
    name: "",
    title: "Sales Director",
    location: "Sales Director, Phoenix, AZ"
  }
];

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px',
  },
  hero: {
    position: 'relative',
    minHeight: '90vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    /* Fallback color shown before video loads */
    background: '#0F1118',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 30% 50%, rgba(227, 30, 36, 0.2) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  heroPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.828-1.415 1.415L51.8 0h2.827z' fill='%23ffffff' fill-opacity='0.03'/%3E%3C/svg%3E")`,
    pointerEvents: 'none',
  },
  heroContent: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
  },
  heroTextContainer: {},
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    background: 'rgba(255, 195, 0, 0.2)',
    border: '2px solid #FFC300',
    borderRadius: '50px',
    padding: '12px 24px',
    color: '#FFC300',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '13px',
    fontWeight: '800',
    letterSpacing: '1.5px',
    marginBottom: '32px',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    lineHeight: '1.1',
    color: '#FFFFFF',
    marginBottom: '28px',
    letterSpacing: '2px',
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '20px',
    lineHeight: '1.7',
    color: '#E5E5E5',
    marginBottom: '40px',
    maxWidth: '900px',
    margin: '0 auto 40px',
  },
  heroStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '48px',
    marginBottom: '48px',
    flexWrap: 'wrap',
  },
  heroStat: {
    textAlign: 'center',
  },
  heroStatNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '48px',
    fontWeight: '700',
    color: '#FFC300',
    marginBottom: '8px',
  },
  heroStatLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '13px',
    color: '#CCC',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  heroCTAs: {
    display: 'flex',
    gap: '20px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '32px',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: 'white',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '20px',
    padding: '20px 40px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(227, 30, 36, 0.4)',
    letterSpacing: '1px',
  },
  btnSecondary: {
    background: 'transparent',
    color: '#0080FF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '20px',
    padding: '18px 38px',
    border: '3px solid #0080FF',
    borderRadius: '8px',
    cursor: 'pointer',
    letterSpacing: '1px',
  },
  guarantee: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    padding: '16px 24px',
    color: '#FFFFFF',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
  },
  scrollIndicator: {
    position: 'absolute',
    bottom: '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#FFC300',
    zIndex: 2,
  },
  explosiveStats: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '60px 0',
  },
  explosiveStatGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
    gap: '24px',
  },
  explosiveStatItem: {
    background: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    padding: '32px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  explosiveStatIcon: {
    flexShrink: 0,
  },
  explosiveStatNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '52px',
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: '1',
  },
  explosiveStatLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
    marginTop: '8px',
  },
  promiseBanner: {
    position: 'relative',
    /* Fallback color before video loads */
    background: '#1A1F3A',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  promiseH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    color: '#FFFFFF',
    lineHeight: '1',
    marginBottom: '32px',
    letterSpacing: '3px',
  },
  promiseText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    lineHeight: '1.7',
    color: '#E5E5E5',
    maxWidth: '900px',
    margin: '0 auto',
  },
  resultsSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '64px',
  },
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    lineHeight: '1.1',
    color: '#1A1F3A',
    marginBottom: '16px',
    letterSpacing: '2px',
  },
  sectionSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '20px',
    color: '#666',
    fontWeight: '600',
  },
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
    gap: '24px',
  },
  resultCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '32px',
    border: '3px solid #E5E5E5',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  resultCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    paddingBottom: '20px',
    borderBottom: '2px solid #F5F5F5',
  },
  resultLocation: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    letterSpacing: '1px',
  },
  resultStats: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    paddingTop: '8px',
  },
  resultStat: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  resultNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '32px',
    fontWeight: '700',
    color: '#E31E24',
    marginBottom: '6px',
    lineHeight: '1',
  },
  resultLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '13px',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600',
  },
  resultDivider: {
    width: '2px',
    height: '60px',
    background: '#E5E5E5',
  },
  servicesSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  servicesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
    gap: '32px',
  },
  serviceCard: {
    background: '#FFFFFF',
    border: '3px solid #E5E5E5',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
  },
  serviceIcon: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
  serviceTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '32px',
    color: '#1A1F3A',
    marginBottom: '16px',
    letterSpacing: '1px',
  },
  serviceDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
    marginBottom: '24px',
  },
  serviceLink: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    fontWeight: '700',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  },
  testimonialsSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  testimonialCard: {
    background: '#FFFFFF',
    borderRadius: '20px',
    padding: '64px 48px',
    maxWidth: '900px',
    margin: '0 auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  },
  starRating: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '32px',
  },
  testimonialQuote: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    lineHeight: '1.8',
    color: '#333',
    textAlign: 'center',
    marginBottom: '40px',
    fontStyle: 'italic',
  },
  testimonialAuthor: {
    textAlign: 'center',
  },
  testimonialName: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    letterSpacing: '1px',
  },
  testimonialTitle: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    color: '#666',
    fontWeight: '600',
    marginTop: '4px',
  },
  testimonialLocation: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#999',
    marginTop: '4px',
  },
  testimonialDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '32px',
  },
  testimonialDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    transition: 'all 0.3s ease',
  },
  finalCTA: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '100px 0',
    textAlign: 'center',
  },
  finalCTAH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    lineHeight: '1.2',
    letterSpacing: '2px',
  },
  finalCTAText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#FFFFFF',
    marginBottom: '48px',
    fontWeight: '600',
  },
};

export default HomePage;
